<template>
    <div
        class="bill-item"
        :class="{
            'has-discount': item.hasDiscount,
            'has-removable-discount': item.hasDiscount && allowDiscountRemoval,
        }"
    >
        <slot name="prepend-header"></slot>

        <OrderItemQuantity
            class="quantity"
            :quantity="currentTotalQuantity"
            :quantityDigits="quantityDigits"
            :indent="true"
        />

        <div class="item-details-container" :class="{ inactive: inactive }">
            <div class="main-details">
                <span class="name"
                    >{{ getNameInLocationLanguage(item, props.showInternalNames) }}<span class="timer" v-if="item.timerStartedDateTime"
                        ><BillItemTimer
                            :startedDateTime="item.timerStartedDateTime"
                            :stoppedDateTime="item.timerStoppedDateTime"
                            :elapsedSeconds="item.timerElapsedSeconds"
                            :elapsedReadDateTime="item.timerElapsedReadDateTime" /></span
                    ><ItemIcons v-if="item.articleId" :articleId="item.articleId"
                /></span>

                <Button
                    v-if="props.showOpenOrderDetailsButton"
                    aria-label="Button"
                    icon="fa fa-info"
                    variant="outlined"
                    rounded
                    @click="
                        (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            props.onClickOpenOrderDetails(props.item.orderId);
                        }
                    "
                />

                <ItemPrice
                    :hasDiscount="item.hasDiscount"
                    :quantity="currentMultiplier"
                    :unitPrice="bill.getLiveUnitPrice(item)"
                    :unitPriceAfterDiscount="bill.getLiveUnitPriceAfterDiscount(item)"
                    :discountType="item.lastDiscountType"
                    @click="onPriceClick"
                />
            </div>
            <div class="subs" v-if="item.options.length">
                <BillItemOptionGroup
                    v-for="optionGroup in item.options"
                    :key="optionGroup.id"
                    :optionGroup="optionGroup"
                    :multiplier="currentMultiplier"
                    :inactive="inactive"
                    :showInternalNames="props.showInternalNames"
                    :allowDiscountRemoval="props.allowDiscountRemoval"
                    :forceDisplayGroupName="props.forceDisplayGroupName"
                />
            </div>
            <div class="comment" v-if="props.showComment && item.comment">
                <span class="comment-text">{{ item.comment.text }}</span>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { Popup } from "@/components/dialog/Popup";
    import type { IBillItem } from "@/data/model/Bill";
    import { useBillStore } from "@/data/stores/bill";
    import { getNameInLocationLanguage } from "@/shared/helpers/ItemNameHelper";
    import { translations } from "@/shared/localization/translations";
    import { computed } from "vue";

    import BillItemOptionGroup from "./BillItemOptionGroup.vue";
    import BillItemTimer from "./BillItemTimer.vue";
    import ItemIcons from "./ItemIcons.vue";
    import ItemPrice from "./ItemPrice.vue";
    import OrderItemQuantity from "./OrderItemQuantity.vue";
    import Button from "primevue/button";

    const bill = useBillStore();

    const props = defineProps<{
        item: IBillItem;
        inactive?: boolean;
        quantityDigits?: number;
        currentQuantity?: number;
        totalQuantity?: number;
        showInternalNames: boolean;
        showComment: boolean;
        allowDiscountRemoval: boolean;
        forceDisplayGroupName?: boolean;
        showOpenOrderDetailsButton: boolean;
        onClickOpenOrderDetails: (orderId: string) => void;
    }>();

    const currentMultiplier = computed(() =>
        props.currentQuantity === undefined ? props.item.quantity : props.currentQuantity
    );
    const currentTotalQuantity = computed(() =>
        props.totalQuantity === undefined ? props.item.quantity : props.totalQuantity
    );

    function onPriceClick(e: Event) {
        if (!props.allowDiscountRemoval) return;
        if (!props.item.hasDiscount) return;
        if (props.item.hasProcessedItemLevelDiscount) {
            e.stopPropagation();
            Popup.show({
                message: translations.discounts.removeAllItemDiscountsBlockedPrompt,
                uniqueId: "itemLevelDiscountsProcessedPopup",
            });
            return;
        }

        e.stopPropagation();
        bill.showConfirmItemDiscountsRemovalPopup(props.item);
    }
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .bill-item {
        font-size: 16px;
        padding: 0.75em 1.25em;
        border-bottom: 1px solid colors.$lightGreyBorder;
        font-weight: 300;
        line-height: 1.25em;
        color: colors.$fontColor;
        display: flex;

        &.has-discount {
            padding-top: 1.225em;

            .main-details {
                margin-bottom: -0.125em;
            }
        }

        &.has-removable-discount {
            .main-details {
                > .item-price {
                    cursor: pointer;
                    transition: opacity 0.2s variables.$easeOutExpo;

                    &:hover {
                        opacity: 0.5;
                    }
                }
            }
        }

        > .item-details-container {
            flex: 1 1 auto;
            color: colors.$fontColor;

            .p-button {
                color: colors.$lightGrey;
                background: transparent;
                border: 1px solid colors.$lightGrey;
                padding: 0;
                min-width: 20px;
                min-height: 20px;
                width: 1.8em !important;
                height: 1.8em !important;
                font-size: 0.75rem !important;
                transition: null !important;
            }

            .p-button:not(:disabled):hover {
                color: colors.$lightGrey !important;
                background: colors.$lightGreyBackground !important;
                border: 1px solid colors.$lightGrey !important;
            }

            .p-button:focus {
                box-shadow: 0 0 0 0.2rem colors.$lightGreyBackground !important;
            }

            &.inactive {
                color: colors.$lightFontColor;
            }

            > .main-details {
                display: flex;

                .name {
                    text-overflow: ellipsis;
                    flex: 1 1 auto;
                    word-break: break-word;

                    // the timer of a price per minute item sits in a row of its own, below the name and above the icons
                    > .timer {
                        display: block;
                        font-size: 14px;

                        + .item-icons {
                            margin-left: 0;
                        }
                    }
                }

                > .item-price {
                    padding-left: 1em;
                }
            }

            > .subs {
                padding: 0.375em 0.125em 0.5em;
                margin-bottom: -0.75em;
            }

            > .comment {
                padding: 0.375em 0.125em 0.5em;
                margin-bottom: -0.75em;

                font-size: 12px;
                font-style: italic;
                font-weight: bold;
                color: colors.$red;
            }
        }
    }
</style>
